<script setup lang="ts">
import { storeToRefs } from "pinia";
import SimpleDropdown from "~/components/Common/Dropdowns/SimpleDropdown.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import Tooltip from "../Tooltip.vue";
import { SessionFileTypeEnum } from "~/utils/enums/session-file-type.enums";
import type { AttendeeInterface } from "~/utils/interfaces/sessions-interfaces";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import type { IUploadFile } from "~/utils/interfaces/documents-interfaces";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { SessionDetailsApi, TrainingPlanDetailsItemTypeEnum, UploadDocumentItemTypeEnum } from "@simbelapp/order-sdk";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useUploadDocumentStore } from "~/store/upload-document/upload-document.store";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";
import type { BasicAttendeeModel } from "@simbelapp/order-sdk";

const { t } = useI18n();

const emit = defineEmits([
  "remove",
  "updateDocumentFileItem",
  "updateDocumentFileType",
  "updateCanUpload",
  "updateDocumentVisibility",
]);

const props = defineProps({
  files: { type: Array as PropType<IUploadFile[]>, required: true },
  defaultAttendees: {
    type: Array as PropType<AttendeeInterface[]>,
    required: true,
  },
  attendees: {
    type: Array as PropType<AttendeeInterface[]>,
    required: false,
    default: () => [],
  },
  bookingDetails: {
    type: Boolean,
    required: false,
  },
  canUpload: {
    type: Boolean,
    required: true,
  },
  /** Options of the `Type` dropdown. */
  documentTypes: {
    type: Array as PropType<SessionFileTypeEnum[]>,
    required: false,
    default: () => Object.values(SessionFileTypeEnum),
  },
  /** Session-wide documents, visible to every attendee: hides the `Participants` and `Visible` columns. */
  sessionWide: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const sessionsStore = useSessionsStore();
const sessionDetailsStore = useSessionDetailsStore();
const { trainingPlanSessionIndividual } = storeToRefs(sessionsStore);
const { sessionDetails } = storeToRefs(sessionDetailsStore);
const attendeesInitial = ref<BasicAttendeeModel[]>([]);
const isBookingContext = ref<boolean>(false);
const currentBookingAttendee = ref<BasicAttendeeModel | null>(null);
const uploadDocumentStore = useUploadDocumentStore();
const { attendees: storeAttendees } = storeToRefs(uploadDocumentStore);

onMounted(() => {
  initializeBookingContext();
  attendeesInitial.value = buildAttendeesList();
});

/**
 * Initialize booking context if applicable
 */
function initializeBookingContext(): void {
  if (trainingPlanSessionIndividual.value?.bookingId) {
    isBookingContext.value = true;
    currentBookingAttendee.value = {
      id: trainingPlanSessionIndividual.value?.bookingId,
      displayValue:
        trainingPlanSessionIndividual.value?.fullName ||
        `${trainingPlanSessionIndividual.value?.firstName || ""} ${trainingPlanSessionIndividual.value?.lastName || ""}`.trim(),
      sessionId: "",
      status: "",
      firstname: "",
      email: "",
      employeeNumber: "",
      lastname: "",
      userId: "",
    };
  }
}

/**
 * Build the complete list of attendees from props
 */
function buildAttendeesList(): BasicAttendeeModel[] {
  const list: BasicAttendeeModel[] = [];
  const isBooking = isBookingContext.value;

  // Add documentItem if not in booking context
  if (!isBooking && props.defaultAttendees.length > 0) {
    props.defaultAttendees.forEach((item) => {
      if (item.id && item.itemType) {
        list.push({
          id: item.id,
          displayValue: item.displayValue,
          sessionId: "",
          status: "",
          firstname: "",
          email: "",
          employeeNumber: "",
          lastname: "",
          userId: "",
        });
      }
    });
  }
  return list;
}

/**
 * Update the document type of a file
 */
function handleDocumentTypeUpdate(file: IUploadFile, newType: string): void {
  emit("updateDocumentFileType", file.id, newType);
}

/**
 * Update the visibility of a document
 */
function handleVisibilityUpdate(file: IUploadFile, adminOnly: boolean): void {
  emit("updateDocumentVisibility", file.id, adminOnly);
}

/**
 * Select an attendee for a document
 */
function handleAttendeeSelection(file: IUploadFile, selectedUser: BasicAttendeeModel): void {
  let itemType = "";
  if (selectedUser.id.startsWith("admin-") || selectedUser.displayValue === "Aucun") {
    itemType = UploadDocumentItemTypeEnum.Session;
  } else if (sessionDetails.value?.itemType === TrainingPlanDetailsItemTypeEnum.Project) {
    itemType = UploadDocumentItemTypeEnum.ProjectUser;
  } else {
    itemType = UploadDocumentItemTypeEnum.Booking;
  }
  emit("updateDocumentFileItem", file.id, selectedUser, itemType);
}

/**
 * Return the display label for an attendee
 */
function getAttendeeLabel(attendee: BasicAttendeeModel): string {
  if (isBookingContext.value && currentBookingAttendee.value) {
    return currentBookingAttendee.value.displayValue;
  }
  return attendee?.displayValue || "";
}

/**
 * Return visibility options for the dropdown
 */
const visibilityOptions = [
  { value: true, label: "Non" },
  { value: false, label: "Oui" },
];

/**
 * Return the selected visibility value
 */
function getVisibilityValue(adminOnly: boolean | undefined): { value: boolean; label: string } | null {
  if (adminOnly === undefined) return null;
  return { value: adminOnly, label: adminOnly ? "Non" : "Oui" };
}

// Watchers
watch(
  () => props.files,
  () => {
    if (isBookingContext.value) {
      props.files.forEach((file) => {
        file.attendee = currentBookingAttendee.value;
      });
    }
    const hasInvalidFile = props.files.some((file) => !file.isGoodSize);
    emit("updateCanUpload", !hasInvalidFile);
  },
  { deep: true },
);

const servicesAPI = async (params: any) => {
  const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
  const feedback = useFeedback();
  if (isBookingContext.value) return;
  try {
    const data = await apiInstance.getBasicAttendees({
      lazyLoad: true,
      enableRolesFilters: false,
      ...params,
      itemId: sessionDetails.value?.id,
      itemType: sessionDetails.value?.itemType,
    });
    if (data) {
      return data;
    }
  } catch {
    feedback.error("Une erreur est survenue", "small");
  }
};
</script>

<template>
  <div class="file_preview">
    <div class="flex flex-col w-full">
      <div class="render_files_preview">
        <table
          v-if="files.length > 0"
          class="table-auto w-full border-separate border-spacing-y-4 body_regular responsive-table"
        >
          <thead>
            <tr>
              <th class="align-middle px-2" :class="sessionWide ? 'w-[316px] text-left' : 'w-2/5 lg:w-1/4 text-center'">
                Fichier
              </th>
              <th class="text-left px-2" :class="sessionWide ? 'w-[276px]' : 'w-1/5 lg:w-1/4'">Type</th>
              <th v-if="!sessionWide" class="w-1/5 lg:w-1/4 text-left px-2">Participants</th>
              <th v-if="!sessionWide" class="w-1/5 lg:w-1/4 text-left flex items-center gap-1 px-2">
                <span>Visible</span>
                <Tooltip :position="'top-middle'" append-to-body>
                  <template #content>
                    <div class="flex w-[300px] gap-[8px]">
                      <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                      <span>
                        Si un document est marqué comme « Non visible », il ne sera visible ni par le participant ni par
                        son manager.
                      </span>
                    </div>
                  </template>
                </Tooltip>
              </th>
              <th class="w-auto text-left"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="file in files" :key="file.id">
              <!-- File name -->
              <td class="px-2 align-middle">
                <div class="flex items-center gap-2 w-full">
                  <SvgIcon
                    class="cursor-pointer justify-center text-primary-blue-color flex-shrink-0"
                    :style="{ height: '16px', width: '16px' }"
                    name="complete"
                  />
                  <div class="w-full file-name-ellipsis" :class="{ 'text-primary-red-color': !file.isGoodSize }">
                    {{ file.file.name }}
                  </div>
                </div>
              </td>

              <!-- Document type -->
              <td class="min-w-[120px] px-2">
                <SimpleDropdown
                  v-model="file.documentType"
                  :options="documentTypes"
                  :searchable="false"
                  :delete="false"
                  class="text_start responsive-dropdown"
                  placeholder="Choisir"
                  @update:model-value="handleDocumentTypeUpdate(file, $event)"
                >
                  <template #singleLabel="{ option }">
                    <p v-if="option === 'session' || option === 'project'">Autre</p>
                    <p v-else>{{ t(`session.${option}`) }}</p>
                  </template>
                  <template #option="{ option }">
                    <span>{{ t(`session.${option}`) }}</span>
                  </template>
                </SimpleDropdown>
              </td>

              <!-- Participants -->
              <td v-if="!sessionWide" class="min-w-[120px] px-2">
                <SimpleDropdown
                  v-model="file.attendee"
                  :options="storeAttendees || []"
                  :disabled="bookingDetails || isBookingContext"
                  searchable
                  :delete="false"
                  class="text_start responsive-dropdown"
                  :services-a-p-i="servicesAPI"
                  :store="uploadDocumentStore"
                  store-object="attendees"
                  label="displayValue"
                  track-by="id"
                  :suggestions="attendeesInitial"
                  @update:model-value="handleAttendeeSelection(file, $event)"
                >
                  <template #singleLabel="{ option }">
                    <p>{{ getAttendeeLabel(option) }}</p>
                  </template>
                  <template #option="{ option }">
                    <span>{{ getAttendeeLabel(option) }}</span>
                  </template>
                </SimpleDropdown>
              </td>

              <!-- Visibility -->
              <td v-if="!sessionWide" class="min-w-[100px] px-2">
                <SimpleDropdown
                  :options="visibilityOptions"
                  :model-value="getVisibilityValue(file.adminOnly)"
                  track-by="value"
                  label="label"
                  :searchable="false"
                  :delete="false"
                  class="text_start responsive-dropdown"
                  placeholder="Choisir"
                  @update:model-value="handleVisibilityUpdate(file, $event?.value)"
                >
                  <template #singleLabel="{ option }">
                    <span>{{ option.label }}</span>
                  </template>
                  <template #option="{ option }">
                    <span>{{ option.label }}</span>
                  </template>
                </SimpleDropdown>
              </td>

              <!-- Delete -->
              <td class="w-12">
                <div class="flex justify-end items-center">
                  <SvgIcon
                    class="cursor-pointer min-w-[16px] min-h-[16px] w-[16px] h-[16px] justify-center"
                    name="delete_trash"
                    style="color: var(--text-secondary-color)"
                    @click="emit('remove', file)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <span v-if="!canUpload" class="text-primary-red-color body_regular pl-[24px]">
    Taille de fichier supérieure à 10 Mo
  </span>
</template>

<style scoped lang="scss">
.file_preview {
  @apply flex items-center w-full;

  img {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
}

.progress_bar {
  border-radius: 4px;
  width: 280px;
  height: 4px;
  color: var(--primary-blue-color);
}

.file_name_column {
  text-align: left;
}

.dropdown_column {
  width: 240px;
  margin-top: 15px;
  margin-left: 5px;
  margin-right: 25px;
}

.text_start {
  margin-right: 5px;
}

.upload_progress {
  display: flex;
}

.file-name-ellipsis {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  line-height: 1.2;
}

.responsive-table {
  min-width: 600px;

  @media (max-width: 1024px) {
    min-width: 500px;
  }

  @media (max-width: 768px) {
    min-width: 450px;
  }
}

.responsive-dropdown {
  width: 100%;
  min-width: 100px;

  @media (max-width: 768px) {
    min-width: 80px;
  }
}
</style>
